import { useMemo, useCallback, memo } from 'react';
import BookingGridBase from '../common/BookingGridBase';
import TimeLinePaintball from './TimeLinePaintball';
import BookingCardPaintball from './BookingCardPaintball';
import BookingCardPaintballv2 from './BookingCardPaintballv2';
import dayjs from 'dayjs';

function GridPaintball({
  bookings,
  rowCount,
  date,
  slotCapacity,
  updateBooking,
  handleOpenModal,
  capacity,
  onBookingShift,
  entertainment,
  onDragStart,
  onDragActivated,
  onDragEnd,
  availableShiftTimes,
  draggedBookingDuration,
  isShiftable,
  isLoadingShiftTimes,
}) {
  const slotMode = useMemo(
    () => date.isSame(dayjs(), 'day') || date.isBefore(dayjs(), 'day'),
    [date]
  );

  const calcAttendance = useCallback(
    list => {
      const times = Array.from({ length: (24 - 9) * 2 + 1 }, (_, idx) => {
        const h = 9 + Math.floor(idx / 2);
        const m = idx % 2 === 0 ? '00' : '30';
        const base = {
          isFull: idx % 2 === 0,
          time: `${h}:${m}`,
          qualifiedTime: new Date(date.get('year'), date.get('month'), date.get('date'), h, m),
          i: idx,
        };
        if (slotMode) {
          return {
            ...base,
            s0: 0,
            frees0: slotCapacity,
            s1: 0,
            frees1: slotCapacity,
            s2: 0,
            frees2: slotCapacity,
            s3: 0,
            frees3: slotCapacity,
          };
        }
        return { ...base, s0: 0, frees0: slotCapacity * 4 };
      });

      for (const bk of list) {
        const { bookingType } = bk;
        const isBlocker = bk.status === 'blocked';
        const startKey = bookingType === 'RegiondoBooking' ? 'eventStart' : 'startTime';
        const endKey = bookingType === 'RegiondoBooking' ? 'eventEnd' : 'endTime';
        const compStart = new Date(bk[startKey]);
        const compEnd = new Date(bk[endKey]);
        const count = bk.participantCount;
        const groupID = bk.pbSpecific?.playerGroupID;

        for (let t of times) {
          if (compStart <= t.qualifiedTime && compEnd > t.qualifiedTime) {
            if (slotMode) {
              if (groupID !== undefined) {
                const freeKey = `frees${groupID}`;
                if (!isBlocker) {
                  const key = `s${groupID}`;
                  t[key] += count;
                }
                t[freeKey] -= count;
              }
            } else {
              if (!isBlocker) {
                t.s0 += count;
              }
              t.frees0 -= count;
            }
          }
        }
      }
      return times;
    },
    [slotMode, slotCapacity, date]
  );

  const renderTimeline = useCallback(
    ({ times, slotWidthPx, showTimeRow, showSlotRows, hideTimeRow }) => (
      <TimeLinePaintball
        times={times}
        slotMode={slotMode}
        renderAvailableSlots={true}
        slotWidth={slotWidthPx}
        showTimeRow={showTimeRow}
        showSlotRows={showSlotRows}
        hideTimeRow={hideTimeRow}
      />
    ),
    [slotMode]
  );

  const renderBookingCard = useCallback(
    bk =>
      bk.bookingType === 'RegiondoBooking' ? (
        <BookingCardPaintball
          key={bk._id}
          booking={bk}
          updateBookingState={updateBooking}
          slotMode={slotMode}
          renderAvailableSlots={true}
        />
      ) : (
        <BookingCardPaintballv2
          key={bk._id}
          booking={bk}
          updateBookingState={updateBooking}
          slotMode={slotMode}
          handleOpenModal={handleOpenModal}
          renderAvailableSlots={true}
          onBookingShift={onBookingShift}
          entertainment={entertainment}
          onDragStart={onDragStart}
          onDragActivated={onDragActivated}
          onDragEnd={onDragEnd}
          availableShiftTimes={availableShiftTimes}
          isShiftable={isShiftable}
        />
      ),
    [
      slotMode,
      updateBooking,
      handleOpenModal,
      onBookingShift,
      entertainment,
      onDragStart,
      onDragActivated,
      onDragEnd,
      availableShiftTimes,
      isShiftable,
    ]
  );

  return (
    <BookingGridBase
      bookings={bookings}
      onOpenBlocker={handleOpenModal}
      blockerTopOffset={slotMode ? 112 : 40}
      rowCount={rowCount}
      date={date}
      slotCapacity={slotCapacity}
      calcAttendance={calcAttendance}
      capacityData={capacity}
      availableShiftTimes={availableShiftTimes}
      draggedBookingDuration={draggedBookingDuration}
      isLoadingShiftTimes={isLoadingShiftTimes}
      renderTimeline={renderTimeline}
      renderBookingCard={renderBookingCard}
      headerHeight={slotMode ? 121 : 50}
    />
  );
}

export default memo(GridPaintball);
